import { useDispatch, useSelector } from 'react-redux';
import { dismissSessionBanner } from './authSlice';
import type { AuthState } from './types';

type RootWithAuth = { auth: AuthState };

const MESSAGES: Record<NonNullable<AuthState['endedReason']>, { title: string; body: string }> = {
  'refresh-failed': {
    title: 'Session ended for your safety',
    body: 'We detected an issue with your session token and signed you out. Please sign in again.',
  },
  expired: {
    title: 'Session expired',
    body: 'For security, you were signed out after a period of inactivity. Please sign in again.',
  },
  manual: { title: '', body: '' },
};

/**
 * Top-of-app banner that surfaces an involuntary sign-out reason. Renders
 * nothing on `manual` logout (which is user-initiated and doesn't need
 * explanation) and after the user dismisses it.
 *
 * Mount once near the top of the layout — it self-positions as a sticky
 * full-width strip just below any fixed header.
 */
export function SessionEndedBanner() {
  const dispatch = useDispatch();
  const reason = useSelector((s: RootWithAuth) => s.auth.endedReason);
  if (!reason || reason === 'manual') return null;
  const msg = MESSAGES[reason];
  return (
    // NON-sticky on purpose — the WebHeader is `sticky top-0 z-40` and
    // a sticky banner here would land at the same top:0 anchor and
    // overlap the menu bar. Letting the banner scroll away naturally
    // is also the intent (it's an alert, not a permanent affordance).
    <div className="relative z-30 border-b border-[var(--color-warning)] bg-[var(--color-warning-light,rgba(245,158,11,0.12))] px-4 py-2 text-sm text-[var(--color-text-primary)]">
      <div className="mx-auto flex max-w-7xl items-start gap-3">
        <div className="flex-1">
          <p className="font-semibold">{msg.title}</p>
          <p className="text-xs text-[var(--color-text-secondary)]">{msg.body}</p>
        </div>
        <button
          type="button"
          onClick={() => dispatch(dismissSessionBanner())}
          className="rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-bg-card-hover)]"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
